/**
 * Sichtbare App-/Kontext-Identität für die Web-Shell (nur Metadaten, keine Routing-Logik).
 * @param {string} route
 * @returns {{ titleKey: string, subtitleKey: string }|null}
 */
import { resolveAppKeyFromRoute } from "../app-catalog/index.js";
import { getAppCatalogEntry } from "../app-catalog/index.js";

const __NO_IDENTITY_ROUTES = new Set([
  "login",
  "auth-callback",
  "public-parent-confirm",
  "public-session-rsvp",
  "public-pay",
  "public-pay-success",
  "public-pay-cancel",
  "superadmin",
  "content-admin",
  "i18n-admin",
  "form-admin",
]);

function __isSuperadminish(route) {
  const r = String(route || "").trim();
  return r === "superadmin" || r.startsWith("superadmin");
}

/** Hub-Unterseiten → Katalog-appKey (Titel/Subtitel aus Marketplace-Metadaten). */
const HUB_ROUTE_TO_APP_KEY = Object.freeze({
  "hub-matchmixer": "matchmixer",
  "hub-schleifchen": "matchmixer",
  "hub-trainerstunden": "trainerstunden",
  "hub-trainer": "trainer",
  "hub-clubmeisterschaft": "clubmeisterschaft",
  "hub-medenspiele": "medenspiele",
  "hub-belegung": "belegung",
  "hub-kalender": "kalender",
  kalender: "kalender",
  "hub-winterdoppel": "winterdoppel",
  "hub-regeltermine": "winterdoppel",
  "hub-serientermine": "winterdoppel",
  winterdoppel: "winterdoppel",
  regeltermine: "winterdoppel",
  serientermine: "winterdoppel",
  "hub-clubhub": "clubhub",
  "hub-minigames": "minigames",
  "hub-tennis-live": "tennis-live",
  tenniswelt: "tennis-live",
});

/** Routen ohne Marketplace-Eintrag: feste i18n-Keys. */
const STATIC_ROUTE_IDENTITY = Object.freeze({
  hub: {
    titleKey: "nav.home",
    subtitleKey: "",
  },
  "hub-ki-assistant": {
    titleKey: "hub.ki.page.title",
    subtitleKey: "hub.ki.page.subtitle",
  },
  settings: {
    titleKey: "settings.hub.title",
    subtitleKey: "settings.hub.subtitle",
  },
  profile: {
    titleKey: "account.profile.settingsTitle",
    subtitleKey: "account.profile.settingsSubtitle",
  },
  "dev-qa": {
    titleKey: "dev.qa.title",
    subtitleKey: "dev.qa.lead",
  },
  "player-matches": {
    titleKey: "hub.mainApp.spielerFinden.title",
    subtitleKey: "hub.mainApp.spielerFinden.subtitle",
  },
  "access-requests-admin": {
    titleKey: "hub.accessRequests.title",
    subtitleKey: "hub.accessRequests.subtitle",
  },
  "admin-people": { titleKey: "hub.admin.people.title", subtitleKey: "" },
  "admin-people-detail": {
    titleKey: "hub.admin.people.title",
    subtitleKey: "",
  },
  "admin-locations": {
    titleKey: "hub.admin.locations.title",
    subtitleKey: "hub.admin.locations.subtitle",
  },
  "feature-selection": {
    titleKey: "featureSelection.marketplace.title",
    subtitleKey: "featureSelection.marketplace.subtitle",
  },
  "access-pending": {
    titleKey: "accessPending.title",
    subtitleKey: "accessPending.subtitle",
  },
  "accept-invite": {
    titleKey: "acceptInvite.title",
    subtitleKey: "acceptInvite.subtitle",
  },
  "hub-app-detail": {
    titleKey: "appShell.identity.marketplaceDetailTitle",
    subtitleKey: "appShell.identity.marketplaceDetailSubtitle",
  },
  clubhub: {
    titleKey: "hub.clubhub.title",
    subtitleKey: "hub.clubhub.subtitle",
  },
  verwaltung: {
    titleKey: "hub.admin.title",
    subtitleKey: "hub.admin.subtitle",
  },
});

/**
 * @param {string} route
 * @returns {{ titleKey: string, subtitleKey: string }|null}
 */
export function getShellAppIdentityFromRoute(route) {
  const r = String(route || "").trim();
  if (!r || __NO_IDENTITY_ROUTES.has(r) || __isSuperadminish(r)) return null;

  const staticId = STATIC_ROUTE_IDENTITY[r];
  if (staticId) return staticId;

  const hubKey = HUB_ROUTE_TO_APP_KEY[r];
  if (hubKey) {
    const entry = getAppCatalogEntry(hubKey);
    if (entry?.titleKey) {
      return {
        titleKey: String(entry.titleKey),
        subtitleKey: String(entry.subtitleKey || entry.descriptionKey || ""),
      };
    }
  }

  const appKey = resolveAppKeyFromRoute(r);
  if (appKey) {
    const entry = getAppCatalogEntry(appKey);
    if (entry?.titleKey) {
      return {
        titleKey: String(entry.titleKey),
        subtitleKey: String(entry.subtitleKey || entry.descriptionKey || ""),
      };
    }
  }

  return null;
}
